import React, { useState, forwardRef } from "react";
import { XIcon } from "@phosphor-icons/react";
import { FactValue, FactDefinition } from "../types";
import { SimpleValueRenderer } from "./FactRenderer";
import InlineEntityCard from "../Cards/InlineEntityCard";
import { openDocumentInViewer } from "../Cards/openInViewer";
import { factValueTone } from "../../design/factValueTone";
import { humanizeEnumKey } from "../../Data/humanizeEnumKey";
import { personChipColorStyles } from "../../internalChip";

interface FactValueChipsProps {
  values: FactValue[];
  onRemove?: (index: number) => void;
  schema: FactDefinition;
  compact?: boolean;
  onNavigateToInput?: () => void;
  // Reserves trailing space on every wrapped row so the absolutely-
  // positioned Source chip from EditableFactRenderer doesn't overlap
  // the right-most chips.
  hasSourceChip?: boolean;
}

const FactValueChips = forwardRef<HTMLDivElement, FactValueChipsProps>(
  ({ values, onRemove, schema, onNavigateToInput, hasSourceChip }, ref) => {
    const [focusedIndex, setFocusedIndex] = useState(-1);

    if (values.length === 0) return null;

    const isEditable = Boolean(onRemove);

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (!isEditable) return;

      switch (e.key) {
        case "ArrowRight":
          e.preventDefault();
          if (focusedIndex < values.length - 1) {
            setFocusedIndex(focusedIndex + 1);
          } else {
            onNavigateToInput?.();
          }
          break;
        case "ArrowLeft":
          e.preventDefault();
          if (focusedIndex > 0) {
            setFocusedIndex(focusedIndex - 1);
          }
          break;
        case "ArrowDown":
          e.preventDefault();
          onNavigateToInput?.();
          break;
        case "Backspace":
        case "Delete":
          e.preventDefault();
          if (focusedIndex >= 0 && onRemove) {
            onRemove(focusedIndex);
            if (values.length <= 1) {
              // Last chip removed, navigate to input
              setFocusedIndex(-1);
              onNavigateToInput?.();
            } else if (focusedIndex >= values.length - 1) {
              setFocusedIndex(values.length - 2);
            }
          }
          break;
      }
    };

    const handleFocus = () => {
      if (isEditable && focusedIndex === -1) {
        setFocusedIndex(0);
      }
    };

    const handleBlur = (e: React.FocusEvent) => {
      // Only reset if focus is leaving the entire chips container
      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
        setFocusedIndex(-1);
      }
    };

    // Mirror ScalarValueRenderer: a labelled enum's chip shows the display
    // label (e.g. document_type "fact_sheet" -> "Fact Sheet"), humanizing
    // keys missing from the map so the raw snake_case key never leaks. The
    // stored value (and the chip container's enum_colors lookup) stays the
    // raw key.
    const chipDisplayValue = (scalar: FactValue["scalar"]) => {
      if (typeof scalar !== "string" || schema.enum_labels == null) {
        return scalar;
      }
      if (scalar === "") return scalar;
      return schema.enum_labels[scalar] ?? humanizeEnumKey(scalar);
    };

    const renderChipContent = (item: FactValue) => {
      // Document ref — not an entity, opens in file viewer
      if (item.document) {
        const doc = item.document;
        return (
          <button
            type="button"
            tabIndex={-1}
            onClick={(e) => {
              e.stopPropagation();
              openDocumentInViewer(doc.id, doc.name, doc.blob_kind);
            }}
            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
          >
            {doc.name}
          </button>
        );
      }

      // Composite value - both scalar and entity
      if (item.scalar !== null && item.scalar !== undefined && item.entity) {
        return (
          <span className="flex flex-wrap items-center gap-1 min-w-0">
            <SimpleValueRenderer
              value={chipDisplayValue(item.scalar)}
              scalarEnd={item.scalar_end}
              scalarNote={item.scalar_note}
              scalarType={schema.scalar_type}
              scalarUnit={schema.scalar_unit}
              isHierarchical={schema.is_hierarchical}
              isEnum={Boolean(schema.scalar_values)}
              chipLabel
            />
            <span className="text-gray-400">•</span>
            <InlineEntityCard entity={item.entity} />
          </span>
        );
      }

      // Entity only
      if (item.entity) {
        return <InlineEntityCard entity={item.entity} />;
      }

      // Scalar only. The chip container below IS the pill; the label renders
      // alone inside it (#12021 — the untoned renderer used to draw its
      // hardcoded grey box inside the tinted chip).
      if (item.scalar !== null && item.scalar !== undefined) {
        return (
          <SimpleValueRenderer
            value={chipDisplayValue(item.scalar)}
            scalarEnd={item.scalar_end}
            scalarNote={item.scalar_note}
            scalarType={schema.scalar_type}
            scalarUnit={schema.scalar_unit}
            isHierarchical={schema.is_hierarchical}
            isEnum={Boolean(schema.scalar_values)}
            chipLabel
          />
        );
      }

      // Rule 5: one mark for an absent value, product-wide.
      return <span style={{ color: "var(--fp-ink-4)" }}>—</span>;
    };

    return (
      <div
        ref={ref}
        className={`flex flex-wrap gap-2${hasSourceChip ? " pr-24" : ""}`}
        tabIndex={isEditable ? 0 : undefined}
        onKeyDown={isEditable ? handleKeyDown : undefined}
        onFocus={isEditable ? handleFocus : undefined}
        onBlur={isEditable ? handleBlur : undefined}
      >
        {values.map((item, index) => {
          let colorKey: string | undefined;
          if (item.scalar !== null && item.scalar !== undefined) {
            colorKey = Array.isArray(item.scalar)
              ? String(item.scalar[item.scalar.length - 1])
              : String(item.scalar);
          }
          // Token-derived tone by the one rule the grid uses
          // (design/factValueTone, #12021): status facts speak the
          // judgement tones, a key outside the fact's declared vocabulary
          // the none tone, every other named value the categorical ramp.
          // Org hexColor never reaches paint.
          //
          // Only a NAMED value takes a hue, exactly as the grid decides it
          // (`cellRenderers`): a list of vintage years is a list of numbers,
          // and colouring 2019 differently from 2020 would say something the
          // design does not mean.
          const leaf = Array.isArray(item.scalar)
            ? item.scalar[item.scalar.length - 1]
            : item.scalar;
          // A composite value (scalar riding with a link) is toned by its
          // scalar, as the grid tones it (#12021); an entity-only value has
          // no colorKey and keeps the neutral wash.
          const toneStyles =
            colorKey && typeof leaf === "string"
              ? factValueTone(schema, colorKey)
              : null;

          const isFocused = isEditable && index === focusedIndex;

          // The editor shows the same value the cell shows, in the same
          // grammar (round-12 correction): the tag wash — 5px radius,
          // tinted ground, no border — with the remove affordance an
          // inline quiet glyph, never a boxed accent ✕.
          // `max-w-full min-w-0`: a flex item refuses to shrink below its
          // content by default, so without a bound a long value made the
          // chip wider than the panel (#12027). With it the chip stops at
          // the row's edge and the label inside wraps.
          let chipClasses =
            "inline-flex items-center gap-1 rounded-[5px] text-sm max-w-full min-w-0";
          let chipStyle: React.CSSProperties = {
            padding: "2px 8px",
          };

          const colorClasses =
            item.entity?.kind === "person"
              ? personChipColorStyles(item.entity.is_internal)
              : toneStyles;
          if (colorClasses) {
            chipClasses += " font-medium";
            chipStyle = {
              ...chipStyle,
              backgroundColor: colorClasses.backgroundColor,
              color: colorClasses.textColor,
            };
          } else {
            chipStyle = {
              ...chipStyle,
              backgroundColor:
                "color-mix(in srgb, var(--fp-ink) 5%, transparent)",
              color: "var(--fp-ink-2)",
            };
          }

          if (isFocused) {
            chipClasses += " ring-2 ring-[var(--fp-ink-3)]";
          }

          return (
            <div
              key={
                item.document?.id ||
                item.entity?.id ||
                item.scalar ||
                `idx-${index}`
              }
              className={chipClasses}
              style={chipStyle}
            >
              {renderChipContent(item)}
              {onRemove && (
                <button
                  tabIndex={-1}
                  onClick={() => onRemove(index)}
                  className="p-0 opacity-60 hover:opacity-100"
                  style={{ color: "currentColor", lineHeight: 0 }}
                  type="button"
                >
                  <XIcon size={12} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    );
  }
);

FactValueChips.displayName = "FactValueChips";

export default FactValueChips;
